import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CONTROLLER_CONSTANTS } from '../constants/controller.constants';
import { ENDPOINT_CONSTANTS } from '../constants/endpoint.constants';
import { DpeTenant } from '../interfaces/tenant.interface';
import { ROUTE_CONSTANTS } from '../constants/route.constants';

interface TenantsApiResponse {
  isSuccess?: boolean;
  data?: DpeTenant[];
}

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly TENANTS_KEY = 'dpe_tenants';

  constructor(private http: HttpClient, private router: Router) {}

  setTenants(tenants: DpeTenant[] | null | undefined): void {
    const list = Array.isArray(tenants) ? tenants : [];
    localStorage.setItem(this.TENANTS_KEY, JSON.stringify(list));
  }

  getTenants(): DpeTenant[] {
    const raw = localStorage.getItem(this.TENANTS_KEY);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  clearTenants(): void {
    localStorage.removeItem(this.TENANTS_KEY);
  }

  primaryTenantName(): string | null {
    return this.getTenants()[0]?.name ?? null;
  }

  hasTenant(name: string | null | undefined): boolean {
    if (!name) {
      return false;
    }
    return this.getTenants().some(t => t.name.toLowerCase() === name.toLowerCase());
  }

  tenantFromUrl(url: string = this.router.url): string | null {
    const path = url.split('?')[0];
    const match = path.match(/^\/([^/]+)\/chat(?:\/|$)/i);
    return match ? decodeURIComponent(match[1]) : null;
  }

  currentTenantName(): string | null {
    return this.tenantFromUrl() || this.primaryTenantName();
  }

  chatCommands(tenantName: string): string[] {
    return ['/', tenantName, 'chat'];
  }

  loadTenants(): Observable<DpeTenant[]> {
    const cached = this.getTenants();
    if (cached.length) {
      return of(cached);
    }

    const url = `${environment.apiUrl}/${CONTROLLER_CONSTANTS.ACCOUNT}/${ENDPOINT_CONSTANTS.GET_MY_TENANTS}`;
    return this.http.get<TenantsApiResponse>(url).pipe(
      map(res => res?.data ?? []),
      tap(tenants => this.setTenants(tenants))
    );
  }

  navigateToTenantChat(tenantName: string): Promise<boolean> {
    return this.router.navigate(this.chatCommands(tenantName)).then(ok => {
      // Chat layout remounts on tenant change via route; identity refresh happens there.
      return ok;
    });
  }

  navigateToPrimaryChat(): Promise<boolean> {
    const name = this.primaryTenantName();
    if (!name) {
      return this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
    }
    return this.navigateToTenantChat(name);
  }
}
